export const DEFAULT_UPDATE_FILTERS = {
  query: "",
  tag: "",
  sort: "date_desc",
};

export const UPDATE_TAG_OPTIONS = [
  { value: "New", label: "Nouveau" },
  { value: "Update", label: "Mise à jour" },
  { value: "Bug", label: "Bug" },
];

export const UPDATE_SORT_OPTIONS = [
  { value: "date_desc", label: "Plus récentes" },
  { value: "date_asc", label: "Plus anciennes" },
  { value: "tag_asc", label: "Tag" },
  { value: "title_asc", label: "Titre A-Z" },
];

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function sanitizeHtml(html = "") {
  if (typeof window === "undefined") return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(html || ""), "text/html");

  doc.querySelectorAll("script, iframe, object, embed").forEach((node) => {
    node.remove();
  });

  doc.querySelectorAll("*").forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || "").toLowerCase();

      if (name.startsWith("on")) {
        node.removeAttribute(attr.name);
      }

      if ((name === "href" || name === "src") && value.startsWith("javascript:")) {
        node.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}

export function parseDateSafe(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

export function formatDateShortFR(value) {
  const date = parseDateSafe(value);

  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
}

export function formatDateLongFR(value) {
  const date = parseDateSafe(value);

  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function getUpdateDate(update) {
  return update?.date_update || update?.created_at || update?.createdAt || null;
}

export function getTagMeta(tag) {
  const value = normalizeText(tag);

  if (value === "bug") {
    return {
      value: "Bug",
      label: "Bug",
      badge:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      dot: "bg-amber-500",
      card:
        "border-amber-200 bg-amber-50/40 dark:border-amber-500/20 dark:bg-amber-500/5",
    };
  }

  if (value === "new" || value === "nouveau") {
    return {
      value: "New",
      label: "Nouveau",
      badge:
        "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
      dot: "bg-sky-500",
      card:
        "border-sky-200 bg-sky-50/40 dark:border-sky-500/20 dark:bg-sky-500/5",
    };
  }

  if (value === "update" || value.includes("mise")) {
    return {
      value: "Update",
      label: "Mise à jour",
      badge:
        "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      dot: "bg-emerald-500",
      card:
        "border-emerald-200 bg-emerald-50/40 dark:border-emerald-500/20 dark:bg-emerald-500/5",
    };
  }

  return {
    value: tag || "",
    label: tag || "—",
    badge:
      "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    dot: "bg-slate-500",
    card:
      "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
  };
}

export function sortUpdates(updates, sort = "date_desc") {
  return safeArray(updates)
    .slice()
    .sort((a, b) => {
      if (sort === "title_asc") {
        return String(a?.title_update || "").localeCompare(
          String(b?.title_update || ""),
          "fr"
        );
      }

      if (sort === "tag_asc") {
        return String(a?.tag_update || "").localeCompare(
          String(b?.tag_update || ""),
          "fr"
        );
      }

      const dateA = parseDateSafe(getUpdateDate(a))?.getTime() || 0;
      const dateB = parseDateSafe(getUpdateDate(b))?.getTime() || 0;

      if (sort === "date_asc") return dateA - dateB;

      return dateB - dateA;
    });
}

export function filterUpdates(updates, filters) {
  const query = normalizeText(filters?.query || "");
  const selectedTag = normalizeText(filters?.tag || "");

  return sortUpdates(updates, filters?.sort).filter((update) => {
    const tag = normalizeText(update?.tag_update || "");

    const searchable = normalizeText(
      `${update?.title_update || ""} ${stripHtml(update?.content_update || "")} ${
        update?.tag_update || ""
      }`
    );

    const matchQuery = !query || searchable.includes(query);
    const matchTag = !selectedTag || tag === selectedTag;

    return matchQuery && matchTag;
  });
}

export function calculateUpdatesStats(updates) {
  const rows = safeArray(updates);

  let newCount = 0;
  let updateCount = 0;
  let bugCount = 0;

  for (const update of rows) {
    const tag = normalizeText(update?.tag_update || "");

    if (tag === "new" || tag === "nouveau") newCount += 1;
    else if (tag === "update" || tag.includes("mise")) updateCount += 1;
    else if (tag === "bug") bugCount += 1;
  }

  const latest = sortUpdates(rows, "date_desc")[0] || null;

  return {
    total: rows.length,
    newCount,
    updateCount,
    bugCount,
    latestDate: latest ? getUpdateDate(latest) : null,
  };
}